import { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { animated, useSpring, easings } from "@react-spring/web";

import { useApiTokenFetcher } from "./auth/useApiToken";
import { ApiClient } from "./api/client";
import { qk } from "./api/keys";
import type { TableSummary, WhoAmI } from "./api/types";

import { LoginRoute } from "./routes/Login";
import { TableViewRoute } from "./routes/TableView";
import { SettingsModal } from "./routes/Settings";
import { Topbar } from "./components/Topbar";
import { Sidebar } from "./components/Sidebar";
import { HeaderActionsProvider } from "./lib/header-actions";
import { InlineEditingProvider } from "./lib/inline-editing";
import { withPendingOp } from "./lib/pending-ops";
import { ActiveDatabaseProvider, useActiveDb, withDb } from "./lib/active-db";

const SIDEBAR_DEFAULT_W = 280;
const SIDEBAR_MIN_W = 200;
const SIDEBAR_MAX_W = 560;
const STORAGE_KEY = "kse-sidebar-width-v1";

export default function App({ apiScope }: { apiScope: string }) {
  const getToken = useApiTokenFetcher(apiScope);
  const api = useMemo(() => new ApiClient(getToken), [getToken]);

  return (
    <>
      <UnauthenticatedTemplate>
        <LoginRoute apiScope={apiScope} />
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <ActiveDatabaseProvider api={api}>
          <InlineEditingProvider>
            <HeaderActionsProvider>
              <AuthedShell api={api} />
            </HeaderActionsProvider>
          </InlineEditingProvider>
        </ActiveDatabaseProvider>
      </AuthenticatedTemplate>
    </>
  );
}

function AuthedShell({ api }: { api: ApiClient }) {
  const [collapsed, setCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => readStoredWidth());
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const { activeId } = useActiveDb();

  // Settings is a self-contained modal — it does NOT use a route, so
  // the underlying app's URL (table view, etc.) stays put while Settings
  // is open and after it's closed.
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Persist width across sessions. Only write when not actively dragging
  // and not collapsed (to avoid storing 0).
  useEffect(() => {
    if (dragging || collapsed) return;
    localStorage.setItem(STORAGE_KEY, String(sidebarWidth));
  }, [sidebarWidth, dragging, collapsed]);

  // Whoami — populates the user's roles so we can show admin-only UI
  // (e.g. the Settings menu item, the /settings routes).
  const meQ = useQuery({
    queryKey: qk.me,
    queryFn: () => api.get<WhoAmI>("/api/me"),
  });
  const isAdmin = !!meQ.data?.roles?.includes("kse-metadata-admins");

  // Tables list is per-database; refetched when the user picks a different
  // database from the topbar switcher.
  const tablesQ = useQuery({
    enabled: !!activeId,
    queryKey: [...qk.tables, activeId],
    queryFn: () =>
      withPendingOp("Loading table list…", (signal) =>
        api.get<TableSummary[]>(withDb("/api/tables", activeId), { signal }),
      ),
  });

  const sidebarSpring = useSpring({
    width: collapsed ? 0 : sidebarWidth,
    config: { duration: 240, easing: easings.easeInOutCubic },
    // While the user is actively dragging the resize handle, skip the
    // spring entirely — width should track the cursor 1:1, not chase it.
    immediate: dragging,
  });

  const startResize = (e: React.MouseEvent) => {
    if (collapsed) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: sidebarWidth };
    setDragging(true);
    const onMove = (mv: MouseEvent) => {
      if (!dragRef.current) return;
      const delta = mv.clientX - dragRef.current.startX;
      const next = clamp(
        dragRef.current.startWidth + delta,
        SIDEBAR_MIN_W,
        SIDEBAR_MAX_W,
      );
      setSidebarWidth(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      dragRef.current = null;
      setDragging(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  return (
    <div className="kse-shell">
      <animated.div
        className="kse-sidebar-wrap"
        style={{
          width: sidebarSpring.width,
          height: "100%",
          flexShrink: 0,
          overflow: "hidden",
          borderRight: "1px solid var(--border)",
          position: "relative",
        }}
      >
        {/* Fixed at sidebarWidth so the contents inside .kse-sidebar
            expand cleanly with the wrapper. The wrapper is the
            animating element (collapses to 0); this inner box is what
            the sidebar lays out within. */}
        <div style={{ width: sidebarWidth, height: "100%", minWidth: sidebarWidth }}>
          <Sidebar
            tables={tablesQ.data}
            loading={tablesQ.isLoading}
            isAdmin={isAdmin}
            user={meQ.data}
            onOpenSettings={() => setSettingsOpen(true)}
          />
        </div>
        {!collapsed && (
          <div
            className="kse-sidebar-resize"
            onMouseDown={startResize}
            data-active={dragging}
            aria-label="Resize sidebar"
            role="separator"
            aria-orientation="vertical"
          />
        )}
      </animated.div>

      <div className="kse-main">
        <Topbar
          sidebarCollapsed={collapsed}
          toggleSidebar={() => setCollapsed((c) => !c)}
        />
        <main className="kse-content">
          <Routes>
            <Route
              path="/"
              element={
                <EmptyState
                  loading={tablesQ.isLoading}
                  hasTables={(tablesQ.data?.length ?? 0) > 0}
                />
              }
            />
            <Route
              path="/tables/:schema/:name"
              element={<TableViewRoute api={api} />}
            />
          </Routes>
        </main>
      </div>

      {/* Settings — sibling to the routed app. Open state lives in this
          shell; the URL never changes when Settings is opened, navigated,
          or closed. */}
      <SettingsModal
        opened={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        api={api}
        isAdmin={isAdmin}
        user={meQ.data}
      />
    </div>
  );
}

function readStoredWidth(): number {
  const raw = Number(localStorage.getItem(STORAGE_KEY));
  if (Number.isFinite(raw) && raw >= SIDEBAR_MIN_W && raw <= SIDEBAR_MAX_W) {
    return raw;
  }
  return SIDEBAR_DEFAULT_W;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function EmptyState({ loading, hasTables }: { loading: boolean; hasTables: boolean }) {
  if (loading) return null;
  return (
    <div className="kse-empty">
      <div>
        <div className="kse-empty__title">
          {hasTables
            ? "Pick a table to begin"
            : "No tables visible to your account"}
        </div>
        <div style={{ fontSize: "var(--t-md)", color: "var(--fg-muted)" }}>
          {hasTables
            ? "Use the sidebar on the left, or press ⌘K to search."
            : "Ask an administrator to grant your Entra group SELECT on a table."}
        </div>
      </div>
    </div>
  );
}
